import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  useRef
} from "react";
import { Attachment, Claim, FileUploadResult } from "../../../../types";
import { bytesToSize } from "../../../utils";
import "../DetailView.css";
import ErrorAlert from "../../../components/error-alert/ErrorAlert";
import RetryButton from "../../../components/error-alert/RetryButton";
import { downloadFileFromBackend } from "../../../util/download";
import { useSelector } from 'react-redux';
import { State } from "../../../../store/state";
import { getFileIcon } from "../../../components/icon/Icon";
import { Alert } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { ButtonWrapper } from "../../styled";
import Modal from "../../../components/Modal";

const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "png", "jpg", "jpeg", "txt", "msg", "eml"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

const getExtension = (filename: string) => filename.split(".").pop()?.toLowerCase() || "";

const stripExtension = (filename: string) => {
  const lastDot = filename.lastIndexOf(".");
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
};

const FAILURE_STATUS_MESSAGES: { [key: string]: string } = {
  INVALID_EXTENSION: "This file type is not supported.",
  FILE_TOO_LARGE: "This file is too large.",
  FILE_EMPTY: "The selected file is empty.",
  INVALID_METADATA: "The name or description provided is invalid.",
  FAILED: "The upload failed. Please try again.",
};

type AttachmentProps = {
  dataSelected: Claim;
  attachments: Attachment[];
  requestGetAttachmentsByClaimId: (id: number) => Promise<any>;
  requestUploadAttachment: (id: number, file: File, name: string, description: string) => Promise<any>;
};
const Attachments: FunctionComponent<AttachmentProps> = props => {

  const accessToken = useSelector((state: State) => state.authentication.accessToken);
  const { dataSelected, attachments, requestGetAttachmentsByClaimId, requestUploadAttachment } = props;
  const [showError, setShowError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{ variant: "success" | "info"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(() => {
    setIsFetching(true);
    setShowError(false);
    requestGetAttachmentsByClaimId(dataSelected.id)
      .catch(() => setShowError(true))
      .finally(() => setIsFetching(false));
  }, [requestGetAttachmentsByClaimId, dataSelected]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const resetUploadForm = () => {
    setSelectedFile(null);
    setName("");
    setDescription("");
    setAcknowledged(false);
    setValidationError(null);
    setUploadErrorMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openUploadModal = () => {
    resetUploadForm();
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    resetUploadForm();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    const extension = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setSelectedFile(null);
      setValidationError(`File type not supported. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setValidationError(`File is too large. Maximum size is ${bytesToSize(MAX_FILE_SIZE_BYTES, 0)}`);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);
    if (!name) {
      setName(stripExtension(file.name));
    }
  };

  const canSubmit = !!selectedFile && !validationError && name.trim().length > 0 && description.trim().length > 0 && acknowledged && !isUploading;

  const handleUploadSubmit = () => {
    if (!canSubmit || !selectedFile) {
      return;
    }

    setIsUploading(true);
    setUploadErrorMessage(null);
    requestUploadAttachment(dataSelected.id, selectedFile, name.trim(), description.trim())
      .then((result: FileUploadResult) => {
        if (result.status === "UPLOADED") {
          setShowUploadModal(false);
          resetUploadForm();
          setUploadResult({
            variant: "success",
            text: result.message || `${result.filename} was uploaded successfully.`
          });
          return;
        }

        if (result.status === "QUEUED") {
          setShowUploadModal(false);
          resetUploadForm();
          setUploadResult({
            variant: "info",
            text: `${result.filename} has been received and is being scanned for malware. It will appear in the list shortly. Please create a task for the Project Manager and include any relevant instructions or context.`
          });
          return;
        }

        setUploadErrorMessage(
          result.message || FAILURE_STATUS_MESSAGES[result.status] || "The upload failed. Please try again."
        );
      })
      .catch(() => setUploadErrorMessage("Error while uploading the attachment. Please try again."))
      .finally(() => setIsUploading(false));
  };

  const getAttachments = () => {
    if (attachments.length === 0 || !attachments) {
      return (<div>Currently no attachments.</div>);
    }

    return (
      <table className="table table-striped">
        <thead>
          <tr>
            <th className="td-style" style={{ padding: "0.35rem", textAlign: "center" }}>

            </th>
            <th className="td-style" style={{ padding: "0.35rem" }} >

            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Date
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }} >
              Description
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }} >
              Filename
            </th>
            <th className="td-style" style={{ padding: "0.35rem", textAlign: "right" }}>
              Size
            </th>
            <th className="td-style" style={{ padding: "0.35rem", textAlign: "center" }} >
              Download
            </th>
          </tr>
        </thead>
        <tbody>
          {attachments.map((attachment: Attachment, __index) => {
            let color = "#fff";
            if (__index % 2 === 0) {
              color = "rgba(0, 0, 0, 0.05)";
            }
            return (
              <>
                <tr key={__index + 1} style={{ backgroundColor: color }}>
                  <td className="td-style" style={{ padding: "0.35rem", textAlign: "center" }}>
                    <div>{__index + 1}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem", textAlign: "center" }}>
                    <div
                      onClick={() => {
                        downloadFileFromBackend(attachment.fileLink, accessToken);
                      }}>
                      {getFileIcon(attachment.fileLink)}
                    </div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>{new Date(attachment.fileCreationDate).toLocaleDateString("en-NZ")}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>
                      {attachment.section}
                    </div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem" }}>
                    <div>
                      {attachment.filename}
                    </div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem", textAlign: "right" }}>
                    <div>{bytesToSize(attachment.filesize, 1)}</div>
                  </td>
                  <td className="td-style" style={{ padding: "0.35rem", textAlign: "center" }}>
                    <div
                      onClick={() => {
                        downloadFileFromBackend(attachment.fileLink, accessToken, true);
                      }}>
                      <i
                        className="fa fa-download"
                        aria-hidden="true"
                        style={{
                          cursor: "pointer",
                          color: "rgb(0, 116, 196)",
                          fontSize: "1rem"
                        }}
                      ></i>
                    </div>
                  </td>
                </tr>
              </>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="detail-wrapper">
      {showUploadModal && (
        <Modal
          title="Upload Attachment"
          primaryActionLabel={isUploading ? "Uploading..." : "Upload"}
          primaryAction={handleUploadSubmit}
          primaryActionDisabled={!canSubmit}
          secondaryActionLabel="Cancel"
          secondaryAction={closeUploadModal}
          width="md"
        >
          <div className="form-group">
            <input
              ref={fileInputRef}
              id="attachment-file"
              type="file"
              onChange={handleFileSelected}
              disabled={isUploading}
              style={{ display: "none" }}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <i className="fas fa-cloud-upload-alt" aria-hidden="true" style={{ marginRight: "0.5rem" }}></i>
                Choose File
              </button>
              <span style={{ marginLeft: "0.75rem" }}>
                {selectedFile ? selectedFile.name : "No file selected"}
              </span>
            </div>
            <small className="form-text text-muted">
              Allowed types: {ALLOWED_EXTENSIONS.join(", ")}. Maximum size {bytesToSize(MAX_FILE_SIZE_BYTES, 0)}.
            </small>
            {validationError && (
              <div style={{ color: "rgb(220,53,69)", marginTop: "0.35rem" }}>{validationError}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="attachment-name">Name *</label>
            <input
              id="attachment-name"
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isUploading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="attachment-description">Description *</label>
            <textarea
              id="attachment-description"
              className="form-control"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isUploading}
            />
          </div>

          <Alert variant="warning">
            By uploading a document, you confirm that you have the right to share this information
            and that it does not contain sensitive personal data beyond what is required for the
            insurance claim. All uploads are logged and may be reviewed for compliance.
          </Alert>

          <div className="form-check">
            <input
              id="attachment-acknowledge"
              type="checkbox"
              className="form-check-input"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={isUploading}
            />
            <label className="form-check-label" htmlFor="attachment-acknowledge">
              I have read and acknowledge the above.
            </label>
          </div>

          {uploadErrorMessage && (
            <div style={{ marginTop: "1rem" }}>
              <ErrorAlert lightMode>
                <span>{uploadErrorMessage}</span>
              </ErrorAlert>
            </div>
          )}
        </Modal>
      )}

      <div className="heading">Attachments</div>

      <Alert variant={"warning"}>
        These documents have been prepared for the insurers use only and are not to be released to a third party (including the insured) without express permission from MPS.
      </Alert>

      {uploadResult && (
        <Alert variant={uploadResult.variant} dismissible onClose={() => setUploadResult(null)}>
          {uploadResult.text}
        </Alert>
      )}

      {isFetching && (
        <div
          className="d-flex justify-content-center"
          style={{
            paddingTop: "1rem"
          }}
        >
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
      {showError && !isFetching && (
        <div
          style={{
            display: "flex",
            flex: "0 0 auto",
            paddingTop: "1rem",
            justifyContent: "center"
          }}
        >
          <ErrorAlert lightMode>
            <span style={{ paddingRight: "1rem" }}>
              Error while loading the attachments of the selected claim. Please
              retry or submit a Support Desk ticket.
            </span>
            <RetryButton retryFunction={loadAttachments} />
          </ErrorAlert>
        </div>
      )}
      {!showError && !isFetching && getAttachments()}

      <ButtonWrapper>
        <Button
          variant="primary"
          style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
          onClick={openUploadModal}
        >
          Upload Attachment
        </Button>
      </ButtonWrapper>
    </div>
  );
};

export default Attachments;
